import { cva, type VariantProps } from 'class-variance-authority'
import { AlertCircle, AlertTriangle, CheckCircle, Info, X } from 'lucide-react'
import * as React from 'react'
import { cn } from './lib/utils'

export type ToastVariant = 'default' | 'success' | 'error' | 'warning' | 'info'

export interface Toast {
  id: string
  title?: string
  message: string
  variant?: ToastVariant
  duration?: number
  action?: {
    label: string
    onClick: () => void
  }
}

const toastVariants = cva(
  'relative flex w-full items-center justify-between space-x-4 overflow-hidden rounded-md border p-4 pr-8 shadow-lg transition-all data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-top-full data-[state=open]:sm:slide-in-from-bottom-full',
  {
    variants: {
      variant: {
        default: 'border bg-background text-foreground',
        success: 'border-green-200 bg-green-50 text-green-800',
        error: 'border-red-200 bg-red-50 text-red-800',
        warning: 'border-orange-200 bg-orange-50 text-orange-800',
        info: 'border-blue-200 bg-blue-50 text-blue-800',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
)

const getToastIcon = (variant: ToastVariant) => {
  switch (variant) {
    case 'success':
      return <CheckCircle className="h-5 w-5 text-green-600" />
    case 'error':
      return <AlertCircle className="h-5 w-5 text-red-600" />
    case 'warning':
      return <AlertTriangle className="h-5 w-5 text-orange-600" />
    case 'info':
      return <Info className="h-5 w-5 text-blue-600" />
    default:
      return <Info className="h-5 w-5 text-foreground" />
  }
}

interface ToastProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof toastVariants> {
  toast: Toast
  onClose: (id: string) => void
}

export const ToastItem = React.forwardRef<HTMLDivElement, ToastProps>(
  ({ className, variant, toast, onClose, ...props }, ref) => {
    const [isVisible, setIsVisible] = React.useState(true)

    React.useEffect(() => {
      if (toast.duration && toast.duration > 0) {
        const timer = setTimeout(() => {
          setIsVisible(false)
          setTimeout(() => onClose(toast.id), 300) // Allow animation to complete
        }, toast.duration)

        return () => clearTimeout(timer)
      }
    }, [toast.duration, toast.id, onClose])

    if (!isVisible) return null

    return (
      <div
        ref={ref}
        className={cn(toastVariants({ variant: toast.variant || variant }), className)}
        {...props}
      >
        <div className="flex items-start space-x-3">
          {getToastIcon(toast.variant || 'default')}
          <div className="flex-1 space-y-1">
            {toast.title && <div className="text-sm font-semibold">{toast.title}</div>}
            <div className="text-sm">{toast.message}</div>
            {toast.action && (
              <button
                onClick={toast.action.onClick}
                className="text-sm underline hover:no-underline mt-2"
              >
                {toast.action.label}
              </button>
            )}
          </div>
        </div>
        <button
          onClick={() => onClose(toast.id)}
          className="absolute right-2 top-2 rounded-md p-1 text-foreground/50 opacity-0 transition-opacity hover:text-foreground focus:opacity-100 focus:outline-none focus:ring-2 group-hover:opacity-100"
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </button>
      </div>
    )
  },
)
ToastItem.displayName = 'ToastItem'

export { toastVariants }
